import { useState, useRef, useEffect, ChangeEvent, DragEvent } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { useNavigate, useParams } from 'react-router-dom';
import { Heading, Text, Button, Input } from '../components/atoms';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { useQueryClient } from '@tanstack/react-query';
import styles from './IcpUploadPage.module.css';
import { apiClient } from '../lib/apiClient';
import { Alert } from '../components/atoms/Alert/Alert';
import { uploadIcpAnalysis } from '../services/icpAnalysisService';
import { useToast } from '../stores/toastStore';
import { getAiLimitErrorKind, getAiLimitResetsAt } from '../services/aiUsageService';
import { formatResetDate } from '../components/aiUsage/AiLimitNotice';
import { icpAnalysesPath, type IcpImportState } from '../lib/icpRoutes';

interface IcpProvider {
  id: string;
  name: string;
  slug: string;
  description?: string;
}

type UploadStep = 'select-provider' | 'select-file' | 'uploading' | 'error';

/** ICP reports: the lab's PDF or its CSV export (browsers report .csv MIME types inconsistently). */
const ACCEPTED_REPORT_TYPES = 'application/pdf,.pdf,text/csv,.csv';

function isAcceptedReportFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.(pdf|csv)$/i.test(file.name);
}

export function IcpUploadPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { aquariumId } = useParams<{ aquariumId: string }>();

  const [step, setStep] = useState<UploadStep>('select-provider');
  const [providers, setProviders] = useState<IcpProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<IcpProvider | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  // Labs whose PDF is read by AI (OsciMotion) can fail on used-up AI credits.
  const [uploadError, setUploadError] = useState<unknown>(null);
  const [loadingProviders, setLoadingProviders] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchProviders = async (): Promise<void> => {
      setLoadingProviders(true);
      try {
        const response = await apiClient.get<{ data: IcpProvider[] }>('/api/v1/icp-providers');
        setProviders(response.data ?? []);
      } catch {
        setProviders([]);
      } finally {
        setLoadingProviders(false);
      }
    };
    void fetchProviders();
  }, []);

  const handleSelectProvider = (provider: IcpProvider): void => {
    setSelectedProvider(provider);
    setStep('select-file');
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  const handleBrowse = (): void => {
    fileInputRef.current?.click();
  };

  const handleUpload = async (): Promise<void> => {
    if (!selectedFile || !selectedProvider || !aquariumId) return;

    setStep('uploading');
    setUploadProgress(0);

    let created;
    try {
      // Simulate progress for UX
      setUploadProgress(30);
      setUploadError(null);
      created = await uploadIcpAnalysis(selectedFile, aquariumId, selectedProvider.id);
      setUploadProgress(100);
    } catch (err) {
      setUploadError(err);
      setStep('error');
      return;
    }

    // Back to the aquarium's ICP page with the new analysis selected; the list
    // (and every other ['icp-analyses', id] query) refetches.
    await queryClient.invalidateQueries({ queryKey: ['icp-analyses', aquariumId] });
    // ICP values are measurements too (latest values on the dashboard / entry page).
    void queryClient.invalidateQueries({ queryKey: ['measurements', aquariumId] });
    if (created.parseWarning) {
      showToast(t('icp.upload.parseWarning'), 'info');
    } else {
      showToast(t('icp.upload.successTitle'), 'success');
    }
    const importState: IcpImportState = {
      icpImport: {
        analysisId: created.id,
        parameterCount: created.parameterCount,
        belowDetection: created.belowDetection ?? [],
      },
    };
    navigate(icpAnalysesPath(aquariumId, created.id), { replace: true, state: importState });
  };

  const uploadErrorText = (): string => {
    const aiLimit = getAiLimitErrorKind(uploadError);
    if (aiLimit === 'unavailable') return t('aiUsage.unavailable');
    if (aiLimit === 'exceeded') {
      const date = formatResetDate(getAiLimitResetsAt(uploadError), i18n.language);
      return `${date !== null ? t('aiUsage.exceeded', { date }) : t('aiUsage.exceededNoDate')} ${t('aiUsage.supportHint')}`;
    }
    return t('icp.upload.errorGeneric');
  };

  const handleBack = (): void => {
    if (step === 'select-file') {
      setStep('select-provider');
      setSelectedProvider(null);
      setSelectedFile(null);
    } else if (step === 'error') {
      setStep('select-file');
    } else {
      navigate(-1);
    }
  };

  const handleRetry = (): void => {
    setStep('select-file');
    setUploadProgress(0);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    const file = e.dataTransfer.files[0] ?? null;
    if (file && isAcceptedReportFile(file)) {
      setSelectedFile(file);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
  };

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="narrow" className={styles.page}>
          <PageHeader
            title={t('icp.upload.title')}
            description={t('icp.upload.subtitle')}
            back={
              <Button
                type="button"
                variant="ghost"
                className={styles.backButton}
                onClick={handleBack}
                aria-label={t('common.back')}
              >
                {t('common.back')}
              </Button>
            }
          />

          {/* Step indicator */}
          <div className={styles.stepIndicator} role="list" aria-label={t('icp.upload.steps')}>
            <div
              className={`${styles.step} ${step === 'select-provider' ? styles.stepActive : styles.stepDone}`}
              role="listitem"
            >
              <span className={styles.stepNumber}>1</span>
              <Text className={styles.stepLabel}>{t('icp.upload.step1')}</Text>
            </div>
            <div className={styles.stepConnector} aria-hidden="true" />
            <div
              className={`${styles.step} ${step === 'select-file' ? styles.stepActive : step === 'select-provider' ? styles.stepPending : styles.stepDone}`}
              role="listitem"
            >
              <span className={styles.stepNumber}>2</span>
              <Text className={styles.stepLabel}>{t('icp.upload.step2')}</Text>
            </div>
            <div className={styles.stepConnector} aria-hidden="true" />
            <div
              className={`${styles.step} ${step === 'uploading' || step === 'error' ? styles.stepActive : styles.stepPending}`}
              role="listitem"
            >
              <span className={styles.stepNumber}>3</span>
              <Text className={styles.stepLabel}>{t('icp.upload.step3')}</Text>
            </div>
          </div>

          {/* Step 1: Select Provider */}
          {step === 'select-provider' && (
            <div className={styles.stepContent}>
              <Heading level={2} className={styles.stepTitle}>
                {t('icp.upload.selectProviderTitle')}
              </Heading>
              <Text className={styles.stepDescription}>
                {t('icp.upload.selectProviderDescription')}
              </Text>

              {loadingProviders ? (
                <div className={styles.loadingState}>
                  <div className={styles.spinner} aria-label={t('common.loading')} />
                  <Text>{t('common.loading')}</Text>
                </div>
              ) : providers.length === 0 ? (
                <div className={styles.emptyState}>
                  <Text>{t('icp.upload.noProviders')}</Text>
                </div>
              ) : (
                <div className={styles.providerGrid}>
                  {providers.map((provider) => (
                    <Button
                      key={provider.id}
                      type="button"
                      variant="ghost"
                      className={styles.providerCard}
                      onClick={() => handleSelectProvider(provider)}
                    >
                      <Heading level={3} className={styles.providerName}>
                        {provider.name}
                      </Heading>
                      {provider.description && (
                        <Text className={styles.providerDescription}>
                          {provider.description}
                        </Text>
                      )}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select File */}
          {step === 'select-file' && selectedProvider && (
            <div className={styles.stepContent}>
              <Heading level={2} className={styles.stepTitle}>
                {t('icp.upload.selectFileTitle')}
              </Heading>
              <Text className={styles.stepDescription}>
                {t('icp.upload.selectFileDescription', { provider: selectedProvider.name })}
              </Text>

              <div
                className={`${styles.dropzone} ${selectedFile ? styles.dropzoneActive : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                role="region"
                aria-label={t('icp.upload.dropzoneLabel')}
              >
                {selectedFile ? (
                  <div className={styles.selectedFile}>
                    <span className={styles.fileIcon} aria-hidden="true">📄</span>
                    <Text className={styles.fileName}>{selectedFile.name}</Text>
                    <Text className={styles.fileSize}>
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </Text>
                    <Button
                      type="button"
                      variant="ghost"
                      className={styles.removeFileButton}
                      onClick={() => setSelectedFile(null)}
                      aria-label={t('icp.upload.removeFile')}
                    >
                      ×
                    </Button>
                  </div>
                ) : (
                  <div className={styles.dropzonePrompt}>
                    <span className={styles.uploadIcon} aria-hidden="true">📁</span>
                    <Text className={styles.dropzoneText}>
                      {t('icp.upload.dropzoneText')}
                    </Text>
                    <Text className={styles.dropzoneHint}>
                      {t('icp.upload.dropzoneHint')}
                    </Text>
                  </div>
                )}
              </div>

              <Input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_REPORT_TYPES}
                className={styles.hiddenInput}
                onChange={handleFileChange}
                aria-label={t('icp.upload.fileInputLabel')}
              />

              <div className={styles.fileActions}>
                <Button
                  variant="secondary"
                  onClick={handleBrowse}
                >
                  {t('icp.upload.browse')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => void handleUpload()}
                  disabled={!selectedFile}
                >
                  {t('icp.upload.uploadButton')}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Uploading */}
          {step === 'uploading' && (
            <div className={styles.stepContent}>
              <Heading level={2} className={styles.stepTitle}>
                {t('icp.upload.uploadingTitle')}
              </Heading>
              <Text className={styles.stepDescription}>
                {t('icp.upload.uploadingDescription')}
              </Text>

              <div className={styles.progressContainer}>
                <div
                  className={styles.progressBar}
                  role="progressbar"
                  aria-valuenow={uploadProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className={styles.progressFill}
                    data-progress={uploadProgress}
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <Text className={styles.progressLabel}>{uploadProgress}%</Text>
              </div>
            </div>
          )}

          {/* Error state */}
          {step === 'error' && (
            <div className={styles.stepContent}>
              <Alert
                title={t('icp.upload.errorTitle')}
                titleLevel={2}
                action={
                  <>
                    <Button variant="secondary" onClick={handleBack}>
                      {t('common.back')}
                    </Button>
                    <Button variant="primary" onClick={handleRetry}>
                      {t('icp.upload.retry')}
                    </Button>
                  </>
                }
              >
                {/* Never the raw backend message — always the translated copy. */}
                {uploadErrorText()}
              </Alert>
            </div>
          )}
        </PageContent>
      }
    />
  );
}

export default IcpUploadPage;
